import * as React from "react";
import * as toastr from "toastr";
import { LogClientDiagnostic } from "source/app/services/ClientDiagnostics";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import { quickJournalThemePresets, ResolveQuickJournalThemeName, type QuickJournalThemeSelectionName } from "../QuickJournalTheme";
import { SunIcon, MoonIcon } from "./QuickJournalThemeIcons";
import { RememberQuickJournalAutoTheme } from "../Themes/QuickJournalAutoPreferences";

export function QuickJournalThemeSwitcher({
    activeThemeName,
    onThemeNameChange,
}: {
    activeThemeName: QuickJournalThemeSelectionName;
    onThemeNameChange: (themeName: QuickJournalThemeSelectionName) => void;
}) {
    const orderedThemeNames: QuickJournalThemeSelectionName[] = [
        "auto",
        "dune",
        "chromaflare",
        "night",
        "cyberpunk",
    ];
    const [hoveredThemeName, setHoveredThemeName] =
        React.useState<QuickJournalThemeSelectionName | undefined>(undefined);
    const [pressedThemeName, setPressedThemeName] =
        React.useState<QuickJournalThemeSelectionName | undefined>(undefined);
    const pressedThemeClearTimeout = React.useRef<number | undefined>(
        undefined
    );

    React.useEffect(() => {
        const clearPressedThemeAfterClick = () => {
            window.clearTimeout(pressedThemeClearTimeout.current);
            pressedThemeClearTimeout.current = window.setTimeout(() => {
                setPressedThemeName(undefined);
                pressedThemeClearTimeout.current = undefined;
            }, 140);
        };
        const clearPressedTheme = () => {
            window.clearTimeout(pressedThemeClearTimeout.current);
            setPressedThemeName(undefined);
            pressedThemeClearTimeout.current = undefined;
        };

        window.addEventListener("pointerup", clearPressedThemeAfterClick);
        window.addEventListener("pointercancel", clearPressedTheme);
        return () => {
            window.clearTimeout(pressedThemeClearTimeout.current);
            window.removeEventListener("pointerup", clearPressedThemeAfterClick);
            window.removeEventListener("pointercancel", clearPressedTheme);
        };
    }, []);

    const isPickerEngaged =
        hoveredThemeName !== undefined || pressedThemeName !== undefined;

    return (
        <div
            style={{
                position: "relative",
                zIndex: 3,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                padding: "4px",
                borderRadius: "18px",
                background: "rgba(0, 0, 0, 0.18)",
                backdropFilter: "blur(5px)",
                boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
                width: "fit-content",
                opacity: isPickerEngaged ? 0.5 : 0.25,
                transition: "opacity 180ms ease",
            }}
        >
            {orderedThemeNames.map((themeName) => {
                const isActive = themeName === activeThemeName;
                const isHovered = themeName === hoveredThemeName;
                const isPressed = themeName === pressedThemeName;
                const theme =
                    themeName === "auto"
                        ? quickJournalThemePresets[
                              ResolveQuickJournalThemeName("auto")
                          ]
                        : quickJournalThemePresets[themeName];
                const label = themeName === "auto" ? "Auto" : theme.label;
                const icon =
                    themeName === "auto" ? (
                        <Glyphicon icon={EGlyphicon.adjust} />
                    ) : themeName === "dune" ? (
                        <SunIcon />
                    ) : themeName === "cyberpunk" ? (
                        <Glyphicon icon={EGlyphicon.flash} />
                    ) : themeName === "chromaflare" ? (
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                            <path d="M12 2 20 12 12 22 4 12Z M4 12H20 M12 2V22" fill="none" stroke="currentColor" strokeWidth="1.3" />
                        </svg>
                    ) : (
                        <MoonIcon />
                    );

                return <React.Fragment key={themeName}>
                    {(themeName === "dune" || themeName === "night") && <span aria-hidden="true" style={{
                        alignSelf: "center", width: "24px", height: "1px", margin: "2px 0",
                        background: "linear-gradient(90deg, transparent, rgba(226, 211, 184, .28), transparent)",
                    }} />}
                    <button
                        key={themeName}
                        type="button"
                        onClick={() => {
                            try {
                                RememberQuickJournalAutoTheme(activeThemeName);
                                RememberQuickJournalAutoTheme(themeName);
                            } catch {
                                toastr.error("Auto theme preference could not be remembered on this device.");
                                LogClientDiagnostic("quickjournal-theme", "auto-preference-storage-unavailable", {}, "error");
                            }
                            onThemeNameChange(themeName);
                        }}
                        onPointerDown={() => {
                            window.clearTimeout(
                                pressedThemeClearTimeout.current
                            );
                            pressedThemeClearTimeout.current = undefined;
                            setPressedThemeName(themeName);
                        }}
                        onMouseEnter={() => setHoveredThemeName(themeName)}
                        onMouseLeave={() => {
                            setHoveredThemeName(undefined);
                        }}
                        onBlur={() => {
                            window.clearTimeout(
                                pressedThemeClearTimeout.current
                            );
                            setPressedThemeName(undefined);
                            pressedThemeClearTimeout.current = undefined;
                        }}
                        aria-pressed={isActive}
                        title={`Switch QuickJournal to ${label}`}
                        style={{
                            appearance: "none",
                            border: `${isPressed ? 3 : 1}px solid ${
                                isHovered
                                    ? theme.switcher.activeBorder
                                    : isActive
                                    ? theme.switcher.activeBorder
                                    : theme.switcher.border
                            }`,
                            background: isHovered
                                ? theme.switcher.activeBackground
                                : isActive
                                ? theme.switcher.activeBackground
                                : theme.switcher.background,
                            color: isHovered
                                ? theme.switcher.activeText
                                : isActive
                                ? theme.switcher.activeText
                                : theme.switcher.text,
                            borderRadius: "999px",
                            width: "34px",
                            height: "34px",
                            padding: 0,
                            cursor: isPressed ? "default" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "16px",
                            lineHeight: 1,
                            transition:
                                "background 120ms ease, color 120ms ease, border-color 120ms ease, border-width 120ms ease, transform 120ms ease, box-shadow 120ms ease",
                            transform:
                                isHovered || isActive
                                    ? "translateY(-1px)"
                                    : "none",
                            boxShadow: isHovered || isActive
                                ? "0 0 0 1px rgba(0, 0, 0, 0.18), 0 6px 14px rgba(0, 0, 0, 0.18)"
                                : "none",
                        }}
                        aria-label={`Switch QuickJournal to ${label}`}
                    >
                        {icon}
                    </button>
                </React.Fragment>;
            })}
        </div>
    );
}
